import React, { useState, useEffect, useCallback } from 'react';

const API = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8000';

const WEEKDAYS = [
  { v: 0, label: 'Mon' }, { v: 1, label: 'Tue' }, { v: 2, label: 'Wed' },
  { v: 3, label: 'Thu' }, { v: 4, label: 'Fri' }, { v: 5, label: 'Sat' },
  { v: 6, label: 'Sun' },
];

function hoursLabel(seconds) {
  const h = seconds / 3600;
  return Number.isInteger(h) ? `${h}h` : `${(seconds / 60) | 0}m`;
}

function Step({ n, title, children, hint }) {
  return (
    <div className="card card-spaced-lg">
      <div className="section-title section-title--inline" style={{ marginBottom: 4 }}>
        <span className="badge badge-blue" style={{ marginRight: 8 }}>{n}</span>{title}
      </div>
      {hint && <div style={{ fontSize: 12, color: 'var(--text-2)', margin: '6px 0 14px' }}>{hint}</div>}
      <div style={{ marginTop: hint ? 0 : 14 }}>{children}</div>
    </div>
  );
}

function Field({ label, children, width }) {
  return (
    <div style={{ flex: width ? `0 0 ${width}` : '1 1 160px', minWidth: 0 }}>
      <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--text-2)',
                      textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: 5 }}>{label}</label>
      {children}
    </div>
  );
}

const row = { display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 14 };

export default function PlanningPage() {
  const [projects, setProjects]       = useState([]);
  const [meta, setMeta]               = useState(null);
  const [assignables, setAssignables] = useState([]);
  const [plans, setPlans]             = useState([]);
  const [history, setHistory]         = useState([]);
  const [historyKey, setHistoryKey]   = useState('');
  const [historyBusy, setHistoryBusy] = useState(false);
  const [error, setError]             = useState(null);
  const [busy, setBusy]               = useState(false);

  // Step 1 — target
  const [projectKey, setProjectKey]   = useState('');
  const [rootMode, setRootMode]       = useState('create');
  const [rootKey, setRootKey]         = useState('');
  const [rootType, setRootType]       = useState('');
  const [rootSummary, setRootSummary] = useState('');
  const [year, setYear]               = useState(String(new Date().getFullYear() + 1));
  const [childType, setChildType]     = useState('');

  // Step 2 — pattern
  const [pattern, setPattern]           = useState('weekly');
  const [startDate, setStartDate]       = useState('');
  const [startTime, setStartTime]       = useState('08:30');
  const [endTime, setEndTime]           = useState('12:30');
  const [weekdays, setWeekdays]         = useState([]);
  const [interval, setIntervalValue]    = useState(1);
  const [customUnit, setCustomUnit]     = useState('weeks');
  const [nth, setNth]                   = useState(2);
  const [nthWeekday, setNthWeekday]     = useState(0);
  const [endMode, setEndMode]           = useState('until');
  const [count, setCount]               = useState(12);
  const [until, setUntil]               = useState('');
  const [summaryTpl, setSummaryTpl]     = useState('{I}-Maintenance {DD}/{MM}/{YYYY}');
  const [assignee, setAssignee]         = useState('');
  const [assigneeAlt, setAssigneeAlt]   = useState('');
  const [billable, setBillable]         = useState('');
  const [intervention, setIntervention] = useState('');
  const [jobTaskId, setJobTaskId]       = useState('');

  // Steps 3 / 4
  const [rows, setRows]         = useState(null);
  const [warnings, setWarnings] = useState([]);
  const [result, setResult]     = useState(null);
  const [emailTo, setEmailTo]     = useState('');
  const [emailBusy, setEmailBusy] = useState(false);
  const [emailMsg, setEmailMsg]   = useState(null);
  const [emailLang, setEmailLang] = useState('en');
  const [calBusy, setCalBusy]     = useState(false);
  const [calResult, setCalResult] = useState(null);

  useEffect(() => {
    fetch(`${API}/api/planning/projects`)
      .then(r => { if (!r.ok) throw new Error('Could not load projects'); return r.json(); })
      .then(setProjects)
      .catch(e => setError(e.message));
  }, []);

  const loadMeta = useCallback((key) => {
    if (!key) { setMeta(null); return; }
    setBusy(true);
    fetch(`${API}/api/planning/meta?project=${encodeURIComponent(key)}`)
      .then(async r => { const d = await r.json(); if (!r.ok) throw new Error(d.detail || 'Failed'); return d; })
      .then(d => {
        setMeta(d);
        const types = d.issue_types || [];
        const plan  = types.find(t => /plan/i.test(t.name) && !t.subtask);
        const task  = types.find(t => /task/i.test(t.name) && t.subtask) || types.find(t => t.subtask);
        setRootType(plan ? plan.name : '');
        setChildType(task ? task.name : '');
        setError(null);
      })
      .catch(e => { setMeta(null); setError(e.message); })
      .finally(() => setBusy(false));

    fetch(`${API}/api/planning/assignable?project=${encodeURIComponent(key)}`)
      .then(r => r.ok ? r.json() : [])
      .then(setAssignables)
      .catch(() => setAssignables([]));

    fetch(`${API}/api/planning/plans?project=${encodeURIComponent(key)}`)
      .then(r => r.ok ? r.json() : [])
      .then(setPlans)
      .catch(() => setPlans([]));
  }, []);

  useEffect(() => { loadMeta(projectKey); }, [projectKey, loadMeta]);

  const loadHistory = useCallback(() => {
    fetch(`${API}/api/planning/history`)
      .then(r => (r.ok ? r.json() : []))
      .then(setHistory)
      .catch(() => setHistory([]));
  }, []);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  const loadPlan = async () => {
    if (!historyKey) return;
    setHistoryBusy(true); setError(null); setEmailMsg(null); setCalResult(null);
    try {
      const r = await fetch(`${API}/api/planning/history/${encodeURIComponent(historyKey)}`);
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Could not load the plan');
      setResult(d);
      setTimeout(() => window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' }), 50);
    } catch (e) { setError(e.message); }
    setHistoryBusy(false);
  };

  // Billable defaults to Yes as soon as the field's options are known. Depends
  // on meta/childType rather than the derived `allowed` object, which is a new
  // reference every render and would loop.
  useEffect(() => {
    const byType = (meta && meta.by_type && meta.by_type[childType]) || {};
    const opts = (byType.allowed && byType.allowed.billable) || [];
    if (!opts.length) return;
    const yes = opts.find(v => /^yes$/i.test(v.value)) || opts[0];
    setBillable(prev => (opts.some(v => v.value === prev) ? prev : yes.value));
  }, [meta, childType]);

  const childMeta  = (meta && meta.by_type && meta.by_type[childType]) || {};
  const allowed    = childMeta.allowed || {};
  const resolved   = childMeta.fields || {};
  const childIsSub = !!(meta && (meta.issue_types || []).find(t => t.name === childType && t.subtask));

  const patternParams = () => ({
    pattern, start_date: startDate, start_time: startTime, end_time: endTime,
    weekdays: pattern === 'weekly' ? weekdays : [],
    interval: Number(interval) || 1,
    custom_unit: customUnit,
    nth: Number(nth) || 1,
    weekday: Number(nthWeekday),
    end_mode: endMode,
    count: Number(count) || 0,
    until,
    summary_template: summaryTpl,
    assignee,
    assignee_alt: assigneeAlt,
    // Drives the {I} token: O for Onsite, R for Remote.
    intervention_type: intervention,
  });

  const doPreview = async () => {
    setError(null); setResult(null); setBusy(true);
    try {
      const r = await fetch(`${API}/api/planning/preview`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(patternParams()),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Preview failed');
      setRows(d.occurrences);
      setWarnings(d.warnings || []);
    } catch (e) { setError(e.message); setRows(null); }
    setBusy(false);
  };

  const editRow = (seq, key, value) =>
    setRows(rs => rs.map(r => (r.seq === seq ? { ...r, [key]: value } : r)));

  const removeRow = seq => setRows(rs => rs.filter(r => r.seq !== seq));

  const execute = async (firstOnly) => {
    if (!rows || !rows.length) return;
    const what = firstOnly
      ? `Create ONE test issue in ${projectKey}?`
      : `Create ${rootMode === 'create' ? '1 plan + ' : ''}${rows.length} subtasks in ${projectKey}?\n\nThis writes to Jira and cannot be undone from here.`;
    if (!window.confirm(what)) return;

    setError(null); setBusy(true);
    try {
      const r = await fetch(`${API}/api/planning/execute`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          project_key: projectKey,
          root_mode: rootMode,
          root_key: rootKey,
          root_issue_type: rootType,
          root_summary: rootSummary,
          year,
          child_issue_type: childType,
          billable,
          intervention_type: intervention,
          job_task_id: jobTaskId,
          first_only: !!firstOnly,
          occurrences: rows,
        }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Execution failed');
      setResult(d);
      loadHistory();      // the new plan becomes loadable straight away
      setCalResult(null);
    } catch (e) { setError(e.message); }
    setBusy(false);
  };

  const resume = async () => {
    if (!result) return;
    setBusy(true); setError(null);
    try {
      const r = await fetch(`${API}/api/planning/batches/${result.batch_id}/resume`, { method: 'POST' });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Resume failed');
      setResult(d);
    } catch (e) { setError(e.message); }
    setBusy(false);
  };

  const sendEmail = async () => {
    if (!result || !emailTo) return;
    setEmailBusy(true); setEmailMsg(null);
    try {
      // A loaded plan resends its current Jira schedule; a fresh run sends its batch.
      const url = result.plan_mode
        ? `${API}/api/planning/history/${encodeURIComponent(result.root_key)}/email`
        : `${API}/api/planning/batches/${result.batch_id}/email`;
      const r = await fetch(url, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ recipient: emailTo, language: emailLang }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Send failed');
      setEmailMsg({ ok: true, text: `✓ Sent ${d.rows} row${d.rows === 1 ? '' : 's'} to ${d.sent_to}` });
    } catch (e) { setEmailMsg({ ok: false, text: e.message }); }
    setEmailBusy(false);
  };

  const isMaintenance = !!(result && /maintenance/i.test(result.child_issue_type || ''));

  const addToAgendas = async () => {
    if (!result) return;
    const n = result.items.filter(i => i.status === 'created').length;
    if (!window.confirm(`Add ${n} appointment${n === 1 ? '' : 's'} to the technicians' Outlook agendas?\n\n`
                        + 'Interventions already in an agenda are skipped. Events are never updated afterwards.')) return;
    setCalBusy(true); setCalResult(null);
    try {
      const url = result.plan_mode
        ? `${API}/api/planning/history/${encodeURIComponent(result.root_key)}/calendar`
        : `${API}/api/planning/batches/${result.batch_id}/calendar`;
      const r = await fetch(url, { method: 'POST' });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Could not add to Outlook');
      setCalResult(d);
    } catch (e) { setCalResult({ error: e.message }); }
    setCalBusy(false);
  };

  const canPreview = projectKey && childType && startDate && (endMode === 'count' ? count : until);
  const canExecute = rows && rows.length && (rootMode === 'existing' ? rootKey : rootSummary);
  const failedCount = result
    ? result.items.filter(i => i.status === 'failed' || i.status === 'pending').length : 0;

  return (
    <div>
      {error && (
        <div style={{ marginBottom: 16, padding: '12px 16px', background: 'var(--critical-light)',
                      borderRadius: 8, color: 'var(--critical)', fontSize: 13 }}>
          {error}
        </div>
      )}

      {history.length > 0 && (
        <div className="card card-spaced" style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <strong style={{ fontSize: 13 }}>Load an existing plan</strong>
          <select className="input" style={{ maxWidth: 440 }} value={historyKey}
                  onChange={e => setHistoryKey(e.target.value)}>
            <option value="">Select a plan created here…</option>
            {history.map(h => (
              <option key={h.root_key} value={h.root_key}>
                {`${h.root_key} — ${h.summary || h.project_key} · ${h.created_items} intervention${h.created_items === 1 ? '' : 's'}`
                  + (h.first_date ? ` · ${h.first_date} → ${h.last_date}` : '')}
              </option>
            ))}
          </select>
          <button className="btn btn-secondary" onClick={loadPlan} disabled={!historyKey || historyBusy}>
            {historyBusy ? 'Loading…' : 'Load'}
          </button>
          <span style={{ fontSize: 11, color: 'var(--text-3)' }}>
            Shows the plan as it currently is in Jira, ready to resend by email.
          </span>
        </div>
      )}

      <Step n="1" title="Target"
            hint="Where the issues go. Create a new plan, or add occurrences to one that already exists.">
        <div style={row}>
          <Field label="Project" width="200px">
            <select className="input" value={projectKey} onChange={e => setProjectKey(e.target.value)}>
              <option value="">Select a project…</option>
              {projects.map(p => <option key={p.key} value={p.key}>{p.key} — {p.name}</option>)}
            </select>
          </Field>
          <Field label="Plan" width="220px">
            <select className="input" value={rootMode} onChange={e => setRootMode(e.target.value)}>
              <option value="create">Create a new plan</option>
              <option value="existing">Use an existing plan</option>
            </select>
          </Field>
          {rootMode === 'existing' ? (
            <Field label="Existing plan" width="320px">
              {plans.length > 0 ? (
                <select className="input" value={rootKey} onChange={e => setRootKey(e.target.value)}>
                  <option value="">Select a plan…</option>
                  {plans.map(p => (
                    <option key={p.key} value={p.key}>
                      {p.key} — {p.summary}{p.status ? ` (${p.status})` : ''}
                    </option>
                  ))}
                </select>
              ) : (
                // No plans found, or the lookup failed: typing a key still works.
                <input className="input mono" value={rootKey} placeholder="UN-1603"
                       onChange={e => setRootKey(e.target.value.toUpperCase())} />
              )}
            </Field>
          ) : (
            <>
              <Field label="Plan issue type" width="180px">
                <select className="input" value={rootType} onChange={e => setRootType(e.target.value)}>
                  {(meta ? meta.issue_types.filter(t => !t.subtask) : []).map(t =>
                    <option key={t.name} value={t.name}>{t.name}</option>)}
                </select>
              </Field>
              <Field label="Plan summary">
                <input className="input" value={rootSummary} placeholder="Maintenance Plan 2027"
                       onChange={e => setRootSummary(e.target.value)} />
              </Field>
              <Field label="Year" width="90px">
                <input className="input" value={year} onChange={e => setYear(e.target.value)} />
              </Field>
            </>
          )}
        </div>
        <div style={row}>
          <Field label="Task issue type" width="200px">
            <select className="input" value={childType} onChange={e => setChildType(e.target.value)}>
              {(meta ? meta.issue_types : []).map(t =>
                <option key={t.name} value={t.name}>{t.name}{t.subtask ? ' (subtask)' : ''}</option>)}
            </select>
          </Field>
        </div>
        {meta && childType && (
          <div style={{ fontSize: 11, color: 'var(--text-2)' }}>
            {childIsSub
              ? 'Created as subtasks of the plan.'
              : 'This type is not a subtask — issues will be created in the project without a parent link.'}
            {' '}Fields resolved: {Object.keys(resolved).length ? Object.keys(resolved).join(', ') : 'none found on this issue type'}.
          </div>
        )}
      </Step>

      <Step n="2" title="Recurrence"
            hint="The time window is wall-clock, and the estimate is derived from it.">
        <div style={row}>
          <Field label="Pattern" width="150px">
            <select className="input" value={pattern} onChange={e => setPattern(e.target.value)}>
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly (same date)</option>
              <option value="nth_weekday">Monthly (nth weekday)</option>
              <option value="quarterly">Quarterly (4×/year)</option>
              <option value="custom">Custom</option>
            </select>
          </Field>
          <Field label="First date" width="150px">
            <input type="date" className="input" value={startDate} onChange={e => setStartDate(e.target.value)} />
          </Field>
          <Field label="Start" width="110px">
            <input type="time" className="input" value={startTime} onChange={e => setStartTime(e.target.value)} />
          </Field>
          <Field label="End" width="110px">
            <input type="time" className="input" value={endTime} onChange={e => setEndTime(e.target.value)} />
          </Field>
          <Field label="Quick slot" width="250px">
            <div style={{ display: 'flex', gap: 6 }}>
              {[['AM', '09:00', '13:00'], ['PM', '13:30', '17:30'], ['Full day', '09:00', '17:00']].map(([label, s, e]) => (
                <button key={label} type="button"
                  className={startTime === s && endTime === e ? 'btn btn-primary' : 'btn btn-secondary'}
                  style={{ padding: '5px 14px', whiteSpace: 'nowrap' }}
                  onClick={() => { setStartTime(s); setEndTime(e); }}>
                  {label}
                </button>
              ))}
            </div>
          </Field>
          {pattern === 'nth_weekday' && (
            <>
              <Field label="Which" width="130px">
                <select className="input" value={nth} onChange={e => setNth(Number(e.target.value))}>
                  <option value={1}>First</option>
                  <option value={2}>Second</option>
                  <option value={3}>Third</option>
                  <option value={4}>Fourth</option>
                  <option value={-1}>Last</option>
                </select>
              </Field>
              <Field label="Weekday" width="140px">
                <select className="input" value={nthWeekday}
                        onChange={e => setNthWeekday(Number(e.target.value))}>
                  {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']
                    .map((d, i) => <option key={d} value={i}>{d}</option>)}
                </select>
              </Field>
            </>
          )}
          {(pattern === 'weekly' || pattern === 'monthly' || pattern === 'nth_weekday' || pattern === 'custom') && (
            <Field label={pattern === 'weekly' ? 'Every N weeks'
                        : (pattern === 'monthly' || pattern === 'nth_weekday') ? 'Every N months'
                        : 'Every N'} width="120px">
              <input type="number" min="1" className="input" value={interval}
                     onChange={e => setIntervalValue(e.target.value)} />
            </Field>
          )}
          {pattern === 'custom' && (
            <Field label="Unit" width="110px">
              <select className="input" value={customUnit} onChange={e => setCustomUnit(e.target.value)}>
                <option value="days">Days</option>
                <option value="weeks">Weeks</option>
                <option value="months">Months</option>
              </select>
            </Field>
          )}
        </div>

        {pattern === 'weekly' && (
          <div style={{ marginBottom: 14 }}>
            <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--text-2)',
                            textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: 6 }}>Weekdays</label>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {WEEKDAYS.map(d => (
                <button key={d.v} type="button"
                  className={weekdays.includes(d.v) ? 'btn btn-primary' : 'btn btn-secondary'}
                  style={{ padding: '5px 11px' }}
                  onClick={() => setWeekdays(w => w.includes(d.v) ? w.filter(x => x !== d.v) : [...w, d.v].sort())}>
                  {d.label}
                </button>
              ))}
            </div>
            <div style={{ fontSize: 11, color: 'var(--text-2)', marginTop: 5 }}>
              None selected uses the weekday of the first date.
            </div>
          </div>
        )}

        <div style={row}>
          <Field label="Ends" width="150px">
            <select className="input" value={endMode} onChange={e => setEndMode(e.target.value)}>
              <option value="until">On a date</option>
              <option value="count">After N occurrences</option>
            </select>
          </Field>
          {endMode === 'until'
            ? <Field label="Last date" width="150px">
                <input type="date" className="input" value={until} onChange={e => setUntil(e.target.value)} />
              </Field>
            : <Field label="Occurrences" width="120px">
                <input type="number" min="1" className="input" value={count}
                       onChange={e => setCount(e.target.value)} />
              </Field>}
          <Field label="Assignee" width="220px">
            <select className="input" value={assignee} onChange={e => setAssignee(e.target.value)}>
              <option value="">Unassigned</option>
              {assignables.map(u => <option key={u.name} value={u.name}>{u.display_name}</option>)}
            </select>
          </Field>
          <Field label="Alternate with" width="220px">
            <select className="input" value={assigneeAlt} onChange={e => setAssigneeAlt(e.target.value)}>
              <option value="">No second technician</option>
              {assignables.filter(u => u.name !== assignee)
                .map(u => <option key={u.name} value={u.name}>{u.display_name}</option>)}
            </select>
          </Field>
          {allowed.billable && (
            <Field label="Billable" width="130px">
              <select className="input" value={billable} onChange={e => setBillable(e.target.value)}>
                <option value="">—</option>
                {allowed.billable.map(v => <option key={v.value} value={v.value}>{v.value}</option>)}
              </select>
            </Field>
          )}
          {allowed.intervention_type && (
            <Field label="Intervention type" width="160px">
              <select className="input" value={intervention} onChange={e => setIntervention(e.target.value)}>
                <option value="">—</option>
                {allowed.intervention_type.map(v => <option key={v.value} value={v.value}>{v.value}</option>)}
              </select>
            </Field>
          )}
        </div>
        <div style={row}>
          <Field label="Summary template">
            <input className="input mono" value={summaryTpl} onChange={e => setSummaryTpl(e.target.value)} />
            <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 4 }}>
              {'{I}'} = O for Onsite, R for Remote · {'{DD} {MM} {YYYY} {HH} {mm}'}
            </div>
          </Field>
          <Field label="JobTaskID">
            <input className="input mono" value={jobTaskId} placeholder="4f9b320d-6023-f111-8340-7ced8d2df311"
                   onChange={e => setJobTaskId(e.target.value.trim())} />
            {meta && childType && !resolved.job_task_id && (
              <div style={{ fontSize: 11, color: 'var(--warn)', marginTop: 4 }}>
                {childType} has no JobTaskID field — this value will not be written.
              </div>
            )}
          </Field>
        </div>

        <button className="btn btn-primary" onClick={doPreview} disabled={!canPreview || busy}>
          {busy ? 'Working…' : 'Preview occurrences'}
        </button>
      </Step>

      {rows && (
        <Step n="3" title={`Preview — ${rows.length} occurrence${rows.length === 1 ? '' : 's'}`}
              hint="Nothing has been sent to Jira yet. Edit or remove any row before creating.">
          {warnings.map((w, i) => (
            <div key={i} style={{ fontSize: 12, color: 'var(--warn)', marginBottom: 6 }}>⚠ {w}</div>
          ))}
          <div className="table-wrap" style={{ maxHeight: 420, overflowY: 'auto', marginBottom: 14 }}>
            <table>
              <thead><tr>
                <th>#</th><th>Date</th><th>Start</th><th>End</th><th>Estimate</th>
                <th>Summary</th><th>Assignee</th><th></th>
              </tr></thead>
              <tbody>
                {rows.map(r => (
                  <tr key={r.seq} style={r.is_weekend ? { background: 'var(--warn-light)' } : undefined}>
                    <td style={{ color: 'var(--text-3)' }}>{r.seq + 1}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      {r.date_display} <span style={{ color: 'var(--text-3)' }}>{r.weekday}</span>
                      {r.is_weekend && <span className="badge badge-medium" style={{ marginLeft: 6 }}>weekend</span>}
                    </td>
                    <td>{r.start_time}</td>
                    <td>{r.end_time}</td>
                    <td>{hoursLabel(r.estimate_seconds)}</td>
                    <td style={{ minWidth: 220 }}>
                      <input className="input" style={{ fontSize: 12, padding: '4px 8px' }} value={r.summary}
                             onChange={e => editRow(r.seq, 'summary', e.target.value)} />
                    </td>
                    <td>
                      <select className="input" style={{ fontSize: 12, padding: '4px 8px' }} value={r.assignee || ''}
                              onChange={e => editRow(r.seq, 'assignee', e.target.value)}>
                        <option value="">Unassigned</option>
                        {assignables.map(u => <option key={u.name} value={u.name}>{u.display_name}</option>)}
                      </select>
                    </td>
                    <td>
                      <button className="btn btn-ghost" style={{ padding: '2px 8px' }}
                              onClick={() => removeRow(r.seq)} title="Remove">✕</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <strong style={{ fontSize: 13 }}>
              Will create {rootMode === 'create' ? '1 plan + ' : ''}{rows.length} {childType || 'task'}
              {rows.length === 1 ? '' : 's'} in {projectKey || '—'}
            </strong>
            <span style={{ flex: 1 }} />
            <button className="btn btn-secondary" onClick={() => execute(true)} disabled={!canExecute || busy}>
              Create first only
            </button>
            <button className="btn btn-primary" onClick={() => execute(false)} disabled={!canExecute || busy}>
              {busy ? 'Creating…' : 'Create in Jira'}
            </button>
          </div>
          {!canExecute && rows.length > 0 && (
            <div style={{ fontSize: 11, color: 'var(--warn)', marginTop: 8 }}>
              {rootMode === 'existing' ? 'Enter the existing plan key first.' : 'Enter a plan summary first.'}
            </div>
          )}
        </Step>
      )}

      {result && (
        <Step n="4" title="Result"
              hint={result.plan_mode
                ? `Loaded plan ${result.root_key}.${result.note ? ' ' + result.note : ''}`
                : result.root_created ? `Plan ${result.root_key} was created.`
                : `Added to existing plan ${result.root_key}.`}>
          <div style={{ display: 'flex', gap: 10, marginBottom: 14, flexWrap: 'wrap', alignItems: 'center' }}>
            {['created', 'skipped', 'failed', 'pending'].map(s => {
              const n = result.items.filter(i => i.status === s).length;
              if (!n) return null;
              return (
                <span key={s} className={`badge ${s === 'created' ? 'badge-low'
                  : s === 'failed' ? 'badge-critical' : 'badge-default'}`}>{n} {s}</span>
              );
            })}
            {failedCount > 0 && (
              <button className="btn btn-secondary" onClick={resume} disabled={busy}>
                {busy ? 'Resuming…' : `Retry ${failedCount} remaining`}
              </button>
            )}
          </div>

          {result.items.some(i => i.status === 'created') && (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 14 }}>
              <input className="input" style={{ maxWidth: 260 }} type="email"
                     placeholder="name@customer.com" value={emailTo}
                     onChange={e => setEmailTo(e.target.value.trim())} />
              <select className="input" style={{ width: 110 }} value={emailLang}
                      onChange={e => setEmailLang(e.target.value)} aria-label="Email language">
                <option value="en">English</option>
                <option value="fr">Français</option>
                <option value="nl">Nederlands</option>
              </select>
              <button className="btn btn-secondary" onClick={sendEmail} disabled={emailBusy || !emailTo}>
                {emailBusy ? 'Sending…' : 'Send by email'}
              </button>
              <span style={{ fontSize: 11, color: 'var(--text-3)' }}>
                Sends the schedule as an Excel file. Only created issues are included.
              </span>
              {emailMsg && (
                <span style={{ fontSize: 12, color: emailMsg.ok ? 'var(--ok)' : 'var(--critical)' }}>
                  {emailMsg.text}
                </span>
              )}
            </div>
          )}

          {isMaintenance && result.items.some(i => i.status === 'created') && (
            <div style={{ marginBottom: 14 }}>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <button className="btn btn-secondary" onClick={addToAgendas} disabled={calBusy}>
                  {calBusy ? 'Adding…' : 'Add to Outlook agendas'}
                </button>
                <span style={{ fontSize: 11, color: 'var(--text-3)' }}>
                  One appointment per intervention, in the assigned technician's agenda. Created once, never updated.
                </span>
              </div>
              {calResult && calResult.error && (
                <div style={{ fontSize: 12, color: 'var(--critical)', marginTop: 8 }}>{calResult.error}</div>
              )}
              {calResult && !calResult.error && (
                <div style={{ fontSize: 12, marginTop: 8 }}>
                  <div style={{ marginBottom: 4 }}>
                    <strong>{calResult.added} added</strong>
                    {calResult.already ? ` · ${calResult.already} already in agenda` : ''}
                    {calResult.skipped ? ` · ${calResult.skipped} skipped` : ''}
                    {calResult.failed ? ` · ${calResult.failed} failed` : ''}
                  </div>
                  {calResult.rows.filter(r => r.status !== 'added').map(r => (
                    <div key={r.issue_key}
                         style={{ color: r.status === 'failed' ? 'var(--critical)' : 'var(--text-2)' }}>
                      {r.issue_key}{r.date ? ` · ${r.date}` : ''}{r.technician ? ` · ${r.technician}` : ''} — {r.detail}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="table-wrap" style={{ maxHeight: 360, overflowY: 'auto' }}>
            <table>
              <thead><tr><th>#</th><th>Summary</th><th>Status</th><th>Issue</th><th>Detail</th></tr></thead>
              <tbody>
                {result.items.map(i => (
                  <tr key={i.seq}>
                    <td style={{ color: 'var(--text-3)' }}>{i.seq + 1}</td>
                    <td>{i.summary}</td>
                    <td>
                      <span className={`badge ${i.status === 'created' ? 'badge-low'
                        : i.status === 'failed' ? 'badge-critical' : 'badge-default'}`}>{i.status}</span>
                    </td>
                    <td className="badge-key-sm">{i.issue_key || '—'}</td>
                    <td style={{ fontSize: 11, color: 'var(--text-2)', maxWidth: 340 }}>{i.error || ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Step>
      )}
    </div>
  );
}
